import useVessel from "@/store/use-vessel";
import TaskCard from "../../components/task-card";
import { useSearchParams } from "react-router-dom";
import { useGetTasks } from "@/services/queries/maintenance";
import { useMemo, useState } from "react";
import { RiRefreshLine } from "@remixicon/react";
import { useTranslation } from "react-i18next";
import Loading from "@/components/custom/loading";
import useAuth from "@/store/use-auth";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";

const INITIAL_TASK_COUNT = 3;

const Tasks = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [showAll, setShowAll] = useState(false);
  const { user } = useAuth();
  const { selectedVessel } = useVessel();
  const canManageMaintenance = useHasPermission(
    PERMISSIONS.Maintenance.Management.Write
  );
  const {
    data: tasksData,
    isPending: isTasksPending,
    isRefetching: isTasksRefetching,
    refetch: refetchTasks,
  } = useGetTasks({
    vesselId: selectedVessel?.id,
    pageSize: 1000000, // To get all tasks
  });

  const tasks = useMemo(() => {
    return tasksData?.items || [];
  }, [tasksData]);

  const date = searchParams.get("date") || new Date();

  const tasksByUser = useMemo(() => {
    if (canManageMaintenance) {
      return tasks;
    }
    return tasks.filter((task) =>
      task.taskDetail.assignees?.some((a) => a.id === user?.sub)
    );
  }, [tasks, user, canManageMaintenance]);

  const displayedTasks = showAll
    ? tasksByUser
    : tasksByUser.slice(0, INITIAL_TASK_COUNT);
  const hasMore = tasksByUser.length > INITIAL_TASK_COUNT;

  return (
    <div className="mt-5 flex flex-col items-center justify-center px-5">
      <div className="flex w-full flex-row items-center justify-between">
        <h2 className="w-full text-start text-[15px] font-semibold leading-[15px] tracking-wide text-white">
          {t("title.tasks").toLocaleUpperCase(i18n.language)}
        </h2>
        <button
          onClick={() => {
            refetchTasks();
          }}
          className="text-white"
          data-testid="refresh-button"
        >
          <RiRefreshLine size={18} />
        </button>
      </div>

      <div className="flex w-full flex-col items-center justify-center gap-[10px] py-3">
        {isTasksPending || isTasksRefetching ? (
          <Loading />
        ) : (
          <>
            {hasMore && showAll && (
              <button
                type="button"
                onClick={() => setShowAll(false)}
                className="w-full rounded-md border border-white/30 bg-white/10 py-2 text-sm font-medium text-white"
                data-testid="show-less-tasks"
              >
                {t("navigation.less")}
              </button>
            )}
            <div className="flex h-full w-full flex-1 flex-col items-start gap-[10px]">
              {displayedTasks.map((task) => (
                <TaskCard
                  redirectPath={`?date=${date}`}
                  key={task.id}
                  task={task}
                />
              ))}
            </div>
            {hasMore && !showAll && (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="w-full rounded-md border border-white/30 bg-white/10 py-2 text-sm font-medium text-white"
                data-testid="show-more-tasks"
              >
                {t("navigation.more")}
              </button>
            )}

            {tasksByUser.length === 0 && (
              <div className="flex w-full flex-col items-center justify-center gap-[10px]">
                <p className="text-white">{t("label.no_tasks_found")}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Tasks;
